import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BsEnvelopeFill, BsTelephoneFill } from "react-icons/bs";
import {
  FaWhatsapp,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa6";
import "./Footer.css";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="footer-2025 border-top" role="contentinfo">
      <div className="container">
        <div className="row gy-5 py-5">
          {/* Logo + pitch */}
          <motion.div
            className="col-lg-4 col-md-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={0}
          >
            <Link
              to="/"
              className="footer-brand d-inline-flex align-items-center"
            >
              <img
                src="/images/logo-Cplatform.png"
                alt="Platform98 — logo"
                width="200"
                height="auto"
                loading="lazy"
                className="footer-logo"
              />
            </Link>
            <p className="footer-text mt-3">
              Platform98 s’occupe de vos démarches administratives et
              juridiques, où que vous soyez.
            </p>
          </motion.div>

          {/* Liens rapides */}
          <motion.nav
            className="col-lg-3 col-md-6"
            aria-label="Liens rapides"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={1}
          >
            <h5 className="footer-title">Liens rapides</h5>
            <ul className="list-unstyled footer-links">
              <li>
                <a className="link-underline" href="/#">
                  Accueil
                </a>
              </li>
              <li>
                <a className="link-underline" href="/#about">
                  À propos
                </a>
              </li>
              <li>
                <a className="link-underline" href="/#services">
                  Nos services
                </a>
              </li>
              <li>
                <a className="link-underline" href="/pricing">
                  Nos tarifs
                </a>
              </li>
              <li>
                <Link className="link-underline" to="/faq">
                  Questions fréquentes
                </Link>
              </li>
              <li>
                <Link className="link-underline" to="/guide">
                  Guide de l'entrepreneur
                </Link>
              </li>
            </ul>
          </motion.nav>

          {/* Contact */}
          <motion.div
            className="col-lg-5 col-md-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={2}
          >
            <h5 className="footer-title">Contacts</h5>

            <ul className="list-unstyled footer-contacts">
              <li className="d-flex align-items-center gap-2">
                <BsEnvelopeFill aria-hidden="true" />
                <a
                  className="link-underline"
                  href="mailto:support@platform98.com"
                  aria-label="Envoyer un email à support@platform98.com"
                >
                  support@platform98.com
                </a>
              </li>

              <li className="d-flex align-items-center gap-2 mt-2">
                <BsTelephoneFill aria-hidden="true" />
                <a
                  className="link-underline"
                  href="tel:+33972119472"
                  aria-label="Appeler le +33 9 72 11 94 72"
                >
                  +33 9 72 11 94 72
                </a>
              </li>

              <li className="d-flex align-items-center gap-2 mt-2">
                <FaWhatsapp aria-hidden="true" className="whatsapp-icon" />
                <a
                  className="link-underline"
                  href="https://wa.me/237677200200?text=Bonjour%20Platform98%2C%20j%E2%80%99aimerais%20en%20savoir%20plus."
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Écrire sur WhatsApp au +237 677 200 200 (nouvel onglet)"
                >
                  +237 677 200 200
                </a>
              </li>
            </ul>

            {/* Réseaux sociaux */}
            <div className="d-flex gap-3 mt-3" aria-label="Réseaux sociaux">
              <a
                className="social-btn"
                href="https://www.facebook.com/profile.php?id=61575217732198"
                aria-label="Facebook (nouvel onglet)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebookF aria-hidden="true" />
              </a>
              <a
                className="social-btn"
                href="https://www.instagram.com/platform.98/"
                aria-label="Instagram (nouvel onglet)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram aria-hidden="true" />
              </a>
              <a
                className="social-btn"
                href="https://www.linkedin.com/company/106676228/"
                aria-label="LinkedIn (nouvel onglet)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedinIn aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        </div>

        <div className="footer-divider" aria-hidden="true"></div>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 py-4">
          <ul className="list-unstyled d-flex flex-wrap align-items-center gap-3 mb-0">
            <li>
              <Link to="/privacy-policy" className="link-underline">
                Politique de confidentialité
              </Link>
            </li>
            <li className="dot" aria-hidden="true">
              •
            </li>
            <li>
              <Link to="/terms-and-conditions" className="link-underline">
                Conditions Générales d’utilisation
              </Link>
            </li>
          </ul>

          <p className="mb-0 small text-opacity">
            © {year} Platform98 — Tous droits réservés
          </p>
        </div>
      </div>

      {/* Données structurées minimalistes (SEO) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Platform98",
            url: "https://platform98.com",
            logo: "https://platform98.com/images/logo-Cplatform.png",
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "customer support",
                email: "support@platform98.com",
                telephone: "+33972119472",
                areaServed: "FR, CM",
              },
            ],
          }),
        }}
      />
    </footer>
  );
};

export default Footer;
